import { useState } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import NewsletterCTA from "../home/components/NewsletterCTA";

const PLATFORMS = [
  {
    label: "Spotify",
    icon: "ri-spotify-fill",
    href: "#",
    bg: "bg-[#1DB954]",
    hoverBg: "hover:bg-[#17a349]",
    text: "text-white",
  },
  {
    label: "Apple Podcasts",
    icon: "ri-apple-fill",
    href: "#",
    bg: "bg-[#8B2FC9]",
    hoverBg: "hover:bg-[#7a28b5]",
    text: "text-white",
  },
  {
    label: "All Platforms",
    icon: "ri-headphone-fill",
    href: "#",
    bg: "bg-amber-700",
    hoverBg: "hover:bg-amber-800",
    text: "text-white",
  },
];

const SUBJECTS = [
  "General Question",
  "Topic Suggestion",
  "Media Inquiry",
  "Other",
];

type FormStatus = "idle" | "submitting" | "success" | "error";

export default function Contact() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const message = data.get("message") as string;
    if (message && message.length > 500) return;

    setStatus("submitting");
    try {
      const body = new URLSearchParams();
      data.forEach((value, key) => body.append(key, value as string));
      const res = await fetch("https://readdy.ai/api/form/d71im48hb0rqicn68i9g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <main className="min-h-screen bg-[#faf7f2] font-sans">
      <Navbar />

      {/* Hero */}
      <section
        className="relative pt-40 pb-20 overflow-hidden"
        style={{ background: "linear-gradient(160deg, #78350f 0%, #92400e 55%, #b45309 100%)" }}
      >
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/6d3daee6f94f72dbe3becf8e2922e30c.jpg"
            alt=""
            className="w-full h-full object-cover object-top opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <span className="inline-block font-sans text-xs font-semibold tracking-widest uppercase text-amber-300 mb-5">
            Get In Touch
          </span>
          <h1 className="font-serif text-5xl lg:text-6xl font-bold text-amber-50 leading-tight mb-6">
            Let&apos;s Connect
          </h1>
          <p className="font-sans text-lg text-amber-100/75 max-w-xl mx-auto leading-relaxed">
            Have a question, a topic suggestion, or just want to say hello?
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-16 items-start">

          {/* Left — Intro & Info */}
          <div className="lg:col-span-2 space-y-10">
            <div>
              <h2 className="font-serif text-3xl font-bold text-stone-800 mb-5 leading-snug">
                I&apos;d Love to Hear From You
              </h2>
              <p className="font-sans text-base text-stone-600 leading-relaxed">
                Have a question, a topic suggestion, or just want to say hello? I&apos;d love to hear from you. Word of mouth is everything for a new podcast — if something resonated with you, reach out and let me know.
              </p>
            </div>

            {/* Info Cards */}
            <div className="space-y-4">
              {[
                {
                  icon: "ri-lightbulb-line",
                  title: "Topic Suggestions",
                  desc: "Have an AI topic you want me to break down? Send it over — listener ideas shape this show.",
                },
                {
                  icon: "ri-question-answer-line",
                  title: "General Questions",
                  desc: "Curious about something I mentioned? Want more context? Ask away, I read everything.",
                },
                {
                  icon: "ri-newspaper-line",
                  title: "Media & Collaboration",
                  desc: "Press inquiries, guest opportunities, or podcast features — use the Media Inquiry subject.",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="flex gap-4 p-5 bg-white rounded-xl border border-amber-100"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-50 flex-shrink-0">
                    <i className={`${item.icon} text-amber-600 text-xl`} />
                  </div>
                  <div>
                    <p className="font-sans text-sm font-semibold text-stone-800 mb-1">{item.title}</p>
                    <p className="font-sans text-sm text-stone-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Quote */}
            <div className="border-l-4 border-amber-400 pl-5 py-1">
              <p className="font-serif italic text-amber-800 text-base leading-relaxed">
                &ldquo;This podcast exists because of people like you. Your voice matters here.&rdquo;
              </p>
              <p className="font-sans text-xs text-stone-500 mt-2 font-medium">— Rich Thurman, Host</p>
            </div>
          </div>

          {/* Right — Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl border border-amber-100 p-8 lg:p-10">
              <h3 className="font-serif text-2xl font-bold text-stone-800 mb-2">Send a Message</h3>
              <p className="font-sans text-sm text-stone-500 mb-8">All fields are required. I&apos;ll get back to you as soon as I can.</p>

              {status === "success" ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-amber-50 mb-5">
                    <i className="ri-check-line text-amber-600 text-3xl" />
                  </div>
                  <h4 className="font-serif text-2xl font-bold text-stone-800 mb-3">Message Sent!</h4>
                  <p className="font-sans text-base text-stone-500 leading-relaxed max-w-sm">
                    Thanks for reaching out. Rich reads every message and will get back to you soon.
                  </p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="mt-8 font-sans text-sm font-semibold text-amber-700 hover:text-amber-900 transition-colors cursor-pointer"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="contact-form"
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  {/* Name + Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="contact-name" className="block font-sans text-sm font-medium text-stone-700 mb-2">
                        Name
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        placeholder="Your full name"
                        className="w-full border border-stone-200 rounded-lg px-4 py-3 font-sans text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-colors bg-[#faf7f2]"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block font-sans text-sm font-medium text-stone-700 mb-2">
                        Email
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        placeholder="you@example.com"
                        className="w-full border border-stone-200 rounded-lg px-4 py-3 font-sans text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-colors bg-[#faf7f2]"
                      />
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label htmlFor="contact-subject" className="block font-sans text-sm font-medium text-stone-700 mb-2">
                      Subject
                    </label>
                    <div className="relative">
                      <select
                        id="contact-subject"
                        name="subject"
                        required
                        defaultValue=""
                        className="w-full border border-stone-200 rounded-lg px-4 py-3 font-sans text-sm text-stone-800 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-colors bg-[#faf7f2] appearance-none cursor-pointer pr-10"
                      >
                        <option value="" disabled>Select a subject...</option>
                        {SUBJECTS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line text-stone-400 text-lg" />
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label htmlFor="contact-message" className="block font-sans text-sm font-medium text-stone-700">
                        Message
                      </label>
                      <span className={`font-sans text-xs ${charCount > 480 ? "text-red-500" : "text-stone-400"}`}>
                        {charCount} / 500
                      </span>
                    </div>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={6}
                      maxLength={500}
                      placeholder="Share your thoughts, question, or topic idea..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full border border-stone-200 rounded-lg px-4 py-3 font-sans text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-colors bg-[#faf7f2] resize-none"
                    />
                  </div>

                  {status === "error" && (
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-red-50 border border-red-100">
                      <i className="ri-error-warning-line text-red-500 text-lg flex-shrink-0" />
                      <p className="font-sans text-sm text-red-600">
                        Something went wrong. Please try again or email directly.
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="w-full font-sans text-sm font-bold px-8 py-4 rounded-lg bg-amber-500 hover:bg-amber-400 active:scale-95 text-amber-950 transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {status === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-base" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line text-base" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Platform Links */}
      <section className="py-16 px-6 lg:px-12 border-t border-amber-100">
        <div className="max-w-7xl mx-auto text-center">
          <span className="inline-block font-sans text-xs font-semibold tracking-widest uppercase text-amber-600 mb-4">
            Already a listener?
          </span>
          <h2 className="font-serif text-3xl font-bold text-stone-800 mb-3">
            Listen Wherever You Podcast
          </h2>
          <p className="font-sans text-base text-stone-500 mb-10 max-w-md mx-auto">
            Subscribe on your favorite platform so you never miss an episode.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            {PLATFORMS.map((p) => (
              <a
                key={p.label}
                href={p.href}
                rel="nofollow"
                className={`flex items-center gap-3 px-7 py-4 rounded-xl font-sans text-sm font-semibold ${p.bg} ${p.hoverBg} ${p.text} transition-all duration-200 cursor-pointer whitespace-nowrap hover:-translate-y-0.5 active:scale-95`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${p.icon} text-lg`} />
                </div>
                {p.label}
              </a>
            ))}
          </div>
          <p className="font-sans text-xs text-stone-400 mt-8">
            Also available on Google Podcasts, Amazon Music, and more.
          </p>
        </div>
      </section>

      <NewsletterCTA />
      <Footer />
    </main>
  );
}
